import { Controller } from "@hotwired/stimulus"

const KEYBOARD_STEP_SECONDS = 5

// Renders playback progress from `player:progress` events and, when
// interactive, lets the user click, drag or use the keyboard to seek.
// Dragging previews the position and only seeks on release. An optional
// tooltip shows the time under the pointer while hovering or dragging.
export default class extends Controller {
  static targets = ["slider", "fill", "elapsed", "duration", "tooltip"]
  static values = { interactive: { type: Boolean, default: true } }

  connect() {
    this.currentTime = 0
    this.duration = 0
    this.dragging = false

    window.addEventListener("player:progress", this.handleProgress)

    if (window.playerController?.isReady) {
      window.playerController.emitProgress()
    } else {
      this.render(0)
    }
  }

  disconnect() {
    window.removeEventListener("player:progress", this.handleProgress)
  }

  initialize() {
    this.handleProgress = this.handleProgress.bind(this)
  }

  handleProgress(event) {
    const { currentTime, duration } = event.detail
    this.currentTime = currentTime
    this.duration = duration

    if (!this.dragging) this.render(currentTime)
  }

  startDrag(event) {
    if (!this.canSeek) return

    // preventDefault stops text selection during the drag but also stops the
    // click from focusing the slider, so focus it for arrow-key seeking.
    event.preventDefault()
    this.sliderTarget.focus()
    this.dragging = true
    this.sliderTarget.dataset.dragging = "true"
    this.sliderTarget.setPointerCapture(event.pointerId)

    const time = this.timeAt(event)
    this.render(time)
    this.showTooltip(time)
  }

  drag(event) {
    if (this.dragging) {
      const time = this.timeAt(event)
      this.render(time)
      this.showTooltip(time)
    } else if (event.pointerType === "mouse" && this.canSeek) {
      this.showTooltip(this.timeAt(event))
    }
  }

  endDrag(event) {
    if (!this.dragging) return

    this.stopDragging()
    if (event.pointerType !== "mouse") this.hideTooltip()
    window.playerController.seek(this.timeAt(event))
  }

  cancelDrag() {
    if (!this.dragging) return

    this.stopDragging()
    this.hideTooltip()
    this.render(this.currentTime)
  }

  leave() {
    if (!this.dragging) this.hideTooltip()
  }

  keydown(event) {
    if (!this.canSeek) return

    const targets = {
      ArrowLeft: this.currentTime - KEYBOARD_STEP_SECONDS,
      ArrowRight: this.currentTime + KEYBOARD_STEP_SECONDS,
      Home: 0,
      End: this.duration
    }
    if (!(event.key in targets)) return

    event.preventDefault()
    window.playerController.seek(targets[event.key])
  }

  showTooltip(time) {
    if (!this.hasTooltipTarget) return

    this.tooltipTarget.style.left = `${this.fractionOf(time) * 100}%`
    this.tooltipTarget.textContent = formatTime(time)
    this.tooltipTarget.hidden = false
  }

  hideTooltip() {
    if (this.hasTooltipTarget) this.tooltipTarget.hidden = true
  }

  stopDragging() {
    this.dragging = false
    delete this.sliderTarget.dataset.dragging
  }

  get canSeek() {
    return this.interactiveValue && this.duration > 0
  }

  timeAt(event) {
    const rect = this.sliderTarget.getBoundingClientRect()
    const fraction = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1)
    return fraction * this.duration
  }

  fractionOf(time) {
    return this.duration > 0 ? Math.min(time / this.duration, 1) : 0
  }

  render(time) {
    this.fillTarget.style.width = `${this.fractionOf(time) * 100}%`

    if (this.hasElapsedTarget) this.elapsedTarget.textContent = formatTime(time)
    if (this.hasDurationTarget) this.durationTarget.textContent = formatTime(this.duration)

    if (this.hasSliderTarget) {
      const slider = this.sliderTarget
      slider.setAttribute("aria-valuemax", Math.floor(this.duration))
      slider.setAttribute("aria-valuenow", Math.floor(time))
      slider.setAttribute("aria-valuetext", `${formatTime(time)} of ${formatTime(this.duration)}`)
      slider.setAttribute("aria-disabled", String(!this.canSeek))
      slider.tabIndex = this.canSeek ? 0 : -1
      slider.classList.toggle("cursor-pointer", this.canSeek)
    }
  }
}

function formatTime(seconds) {
  const total = Math.floor(seconds)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = String(total % 60).padStart(2, "0")

  return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${secs}` : `${minutes}:${secs}`
}
